import type {
  Phase6OptimizationPlanContent,
  Phase6ReviewDisposition,
} from "@/integrations/multiresume/ai/optimization-plan";

type MarkOptimizationPlanItemStatusInput = {
  resumeId: string;
  planId?: string;
  sectionId: "summary" | "experience" | "projects";
  itemId: string;
  acceptanceMode: "direct" | "edited";
};

export type AiEditSaveRecommendation = {
  planId?: string;
  itemId: string;
  proposalType?: "update_item" | "hide_in_target" | "restore_item";
  draft?: unknown;
};

export type AiEditAcceptanceMode = "direct" | "edited";

type MarkOptimizationPlanItemIgnoredInput = Omit<MarkOptimizationPlanItemStatusInput, "acceptanceMode"> & {
  disposition: Phase6ReviewDisposition;
};

export type OptimizationPlanMutationResponse = {
  applied?: boolean;
  ignored?: boolean;
  activePlan: {
    id: string;
    content: Phase6OptimizationPlanContent;
  } | null;
} | null;

export type PersistAiEditProposalAfterSaveResult =
  | "saved_without_proposal"
  | "proposal_marked"
  | "proposal_replayed"
  | "proposal_conflict"
  | "save_failed";

type ProposalTerminalStatus = "applied" | "edited" | "ignored";

function provesTerminalStatus(
  response: OptimizationPlanMutationResponse,
  recommendation: AiEditSaveRecommendation,
  sectionId: "summary" | "experience" | "projects",
  statuses: ProposalTerminalStatus[],
  expectedDisposition?: Phase6ReviewDisposition,
) {
  const activePlan = response?.activePlan;
  if (!recommendation.planId || !activePlan || activePlan.id !== recommendation.planId) return false;

  const matches = activePlan.content.items.filter(
    (item) =>
      item.sectionId === sectionId &&
      item.itemId === recommendation.itemId &&
      Boolean(
        item.status &&
        statuses.includes(item.status as ProposalTerminalStatus) &&
        (expectedDisposition === undefined || item.reviewDisposition === expectedDisposition),
      ),
  );
  return matches.length === 1;
}

export function resolveOptimizationPlanMutation(
  response: OptimizationPlanMutationResponse,
  kind: "applied" | "ignored",
  recommendation: AiEditSaveRecommendation,
  sectionId: "summary" | "experience" | "projects",
  expectedDisposition: Phase6ReviewDisposition = "generic_ignore",
): "proposal_marked" | "proposal_replayed" | "proposal_conflict" {
  const wasMarked = response?.[kind] === true;
  if (wasMarked) return "proposal_marked";

  const wasExactReplay =
    response?.[kind] === false &&
    provesTerminalStatus(
      response,
      recommendation,
      sectionId,
      kind === "applied" ? ["applied", "edited"] : ["ignored"],
      kind === "ignored" ? expectedDisposition : undefined,
    );
  return wasExactReplay ? "proposal_replayed" : "proposal_conflict";
}

export async function persistAiEditProposalAfterResumeSave(input: {
  resumeId: string;
  recommendation?: AiEditSaveRecommendation;
  sectionId: "summary" | "experience" | "projects";
  acceptanceMode?: AiEditAcceptanceMode;
  flushResumeSync: () => Promise<boolean>;
  markOptimizationPlanItemApplied: (
    markInput: MarkOptimizationPlanItemStatusInput,
  ) => Promise<OptimizationPlanMutationResponse>;
  invalidateEditorContext: () => Promise<void> | void;
}): Promise<PersistAiEditProposalAfterSaveResult> {
  const didSave = await input.flushResumeSync();
  if (!didSave) return "save_failed";

  const recommendation = input.recommendation;
  const isStateProposal =
    recommendation?.proposalType === "hide_in_target" || recommendation?.proposalType === "restore_item";
  if (!recommendation?.draft && !isStateProposal) return "saved_without_proposal";

  const response = await input.markOptimizationPlanItemApplied({
    resumeId: input.resumeId,
    planId: recommendation.planId,
    sectionId: input.sectionId,
    itemId: recommendation.itemId,
    acceptanceMode: input.acceptanceMode ?? "edited",
  });
  const result = resolveOptimizationPlanMutation(response, "applied", recommendation, input.sectionId);
  await input.invalidateEditorContext();
  return result;
}

export async function persistAiEditProposalIgnoredAfterResumeSave(input: {
  resumeId: string;
  recommendation?: AiEditSaveRecommendation;
  disposition?: Phase6ReviewDisposition;
  sectionId: "summary" | "experience" | "projects";
  flushResumeSync: () => Promise<boolean>;
  markOptimizationPlanItemIgnored: (
    markInput: MarkOptimizationPlanItemIgnoredInput,
  ) => Promise<OptimizationPlanMutationResponse>;
  invalidateEditorContext: () => Promise<void> | void;
}): Promise<PersistAiEditProposalAfterSaveResult> {
  const didSave = await input.flushResumeSync();
  if (!didSave) return "save_failed";

  const recommendation = input.recommendation;
  if (!recommendation) return "saved_without_proposal";

  const response = await input.markOptimizationPlanItemIgnored({
    resumeId: input.resumeId,
    planId: recommendation.planId,
    sectionId: input.sectionId,
    itemId: recommendation.itemId,
    disposition: input.disposition ?? "generic_ignore",
  });
  const result = resolveOptimizationPlanMutation(
    response,
    "ignored",
    recommendation,
    input.sectionId,
    input.disposition ?? "generic_ignore",
  );
  await input.invalidateEditorContext();
  return result;
}
